/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import type { Skill, SkillCategory, SkillDomain, SkillLevel, SkillStatus } from '@/types/skillKnowledge';

const skillSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(200, 'Name must be less than 200 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(2000, 'Description must be less than 2000 characters'),
  summary: z.string().max(500, 'Summary must be less than 500 characters').optional(),
  category: z.enum(['TECHNICAL', 'SOFT_SKILLS', 'LEADERSHIP', 'MANAGEMENT', 'CREATIVE', 'ANALYTICAL', 'COMMUNICATION', 'DOMAIN_SPECIFIC', 'CROSS_FUNCTIONAL', 'CORE_COMPETENCY', 'SPECIALIZED']),
  domain: z.enum(['PROGRAMMING', 'DATA_ANALYSIS', 'DATA_SCIENCE', 'SOFTWARE_ENGINEERING', 'DESIGN', 'PROJECT_MANAGEMENT', 'PRODUCT_MANAGEMENT', 'DEVOPS', 'CLOUD_INFRASTRUCTURE', 'CYBER_SECURITY', 'AI_MACHINE_LEARNING', 'GENAI_AGENTICAI', 'ROBOTICS', 'NETWORKING', 'DATABASES', 'QUALITY_ASSURANCE', 'TECHNICAL_WRITING', 'RESEARCH', 'EDUCATION_TRAINING', 'BUSINESS_ANALYSIS', 'MARKETING', 'SALES', 'CUSTOMER_SERVICE', 'HUMAN_RESOURCES', 'FINANCE', 'OPERATIONS']),
  availableSkillLevels: z.array(z.enum(['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'])).min(1, 'At least one skill level is required'),
  status: z.enum(['DRAFT', 'ACTIVE', 'UNDER_REVIEW', 'ARCHIVED', 'DEPRECATED', 'MERGED']).optional(),
  isActive: z.boolean().optional().default(true),
  tags: z.string().optional(), // Comma-separated
  parentSkillId: z.string().optional(),
  prerequisiteSkills: z.array(z.string()).optional(),
  enablesSkills: z.array(z.string()).optional(),
  expectedLearningHours: z.number().min(1).max(10000).optional(),
  difficultyRating: z.number().min(1).max(10).optional(),
});

type SkillFormData = z.infer<typeof skillSchema>;

const SKILL_LEVELS: SkillLevel[] = ['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'];
const CATEGORIES: SkillCategory[] = ['TECHNICAL', 'SOFT_SKILLS', 'LEADERSHIP', 'MANAGEMENT', 'CREATIVE', 'ANALYTICAL', 'COMMUNICATION', 'DOMAIN_SPECIFIC', 'CROSS_FUNCTIONAL', 'CORE_COMPETENCY', 'SPECIALIZED'];
const DOMAINS: SkillDomain[] = ['PROGRAMMING', 'DATA_ANALYSIS', 'DATA_SCIENCE', 'SOFTWARE_ENGINEERING', 'DESIGN', 'PROJECT_MANAGEMENT', 'PRODUCT_MANAGEMENT', 'DEVOPS', 'CLOUD_INFRASTRUCTURE', 'CYBER_SECURITY', 'AI_MACHINE_LEARNING', 'GENAI_AGENTICAI', 'ROBOTICS', 'NETWORKING', 'DATABASES', 'QUALITY_ASSURANCE', 'TECHNICAL_WRITING', 'RESEARCH', 'EDUCATION_TRAINING', 'BUSINESS_ANALYSIS', 'MARKETING', 'SALES', 'CUSTOMER_SERVICE', 'HUMAN_RESOURCES', 'FINANCE', 'OPERATIONS'];

export const SkillFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const form = useForm<SkillFormData>({
    resolver: zodResolver(skillSchema) as any,
    defaultValues: {
      name: '',
      description: '',
      summary: '',
      category: 'TECHNICAL',
      domain: 'PROGRAMMING',
      availableSkillLevels: ['BASIC'],
      status: 'DRAFT',
      isActive: true,
      tags: '',
      prerequisiteSkills: [],
      enablesSkills: [],
    },
  });

  const { fields: prerequisiteFields, append: appendPrerequisite, remove: removePrerequisite, replace: replacePrerequisites } = useFieldArray({
    control: form.control as any,
    name: 'prerequisiteSkills' as any,
  });

  const { fields: enablesFields, append: appendEnables, remove: removeEnables, replace: replaceEnables } = useFieldArray({
    control: form.control as any,
    name: 'enablesSkills' as any,
  });

  // Fetch all skills for dropdowns (exclude current skill in edit mode)
  const {
    data: skillsResponse,
    isLoading: isLoadingSkills,
    error: _skillsError,
  } = useQuery({
    queryKey: ['skills', 'all'],
    queryFn: async () => {
      // Fetch a large page size to get all skills
      const response = await skillApi.list({ page: 0, size: 1000, sortBy: 'name', sortDirection: 'asc' });
      return response;
    },
    retry: 1, // Only retry once on error
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get available skills (exclude current skill in edit mode to prevent circular references)
  const availableSkills = useMemo(() => {
    if (!skillsResponse?.content) return [];
    const skills = skillsResponse.content
      .filter((s) => s.id) // Only include skills with valid IDs
      .filter((s) => !isEdit || s.id !== id); // Exclude current skill in edit mode
    return skills.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [skillsResponse, isEdit, id]);

  // Fetch skill data for edit mode
  const {
    data: _skill,
    isLoading: isLoadingSkill,
    error: skillError,
  } = useEntity<Skill>({
    fetchFn: (skillId: string) => skillApi.getById(skillId),
    entityId: id,
    entityName: 'skill',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      // Prepare prerequisite and enables skills arrays
      const prerequisiteSkillsArray = (data.prerequisiteSkills && Array.isArray(data.prerequisiteSkills) && data.prerequisiteSkills.length > 0)
        ? data.prerequisiteSkills.filter(Boolean)
        : [];
      
      const enablesSkillsArray = (data.enablesSkills && Array.isArray(data.enablesSkills) && data.enablesSkills.length > 0)
        ? data.enablesSkills.filter(Boolean)
        : [];
      
      // Reset form with skill data
      const formData = {
        name: data.name || '',
        description: data.description || '',
        summary: data.summary || '',
        category: (data.category || 'TECHNICAL') as SkillCategory,
        domain: (data.domain || 'PROGRAMMING') as SkillDomain,
        availableSkillLevels: (data.availableSkillLevels && data.availableSkillLevels.length > 0 
          ? data.availableSkillLevels 
          : ['BASIC']) as SkillLevel[],
        status: (data.status || 'DRAFT') as SkillStatus,
        isActive: data.isActive ?? true,
        tags: data.tags && data.tags.length > 0 ? data.tags.join(', ') : '',
        parentSkillId: data.parentSkillId || undefined,
        prerequisiteSkills: prerequisiteSkillsArray,
        enablesSkills: enablesSkillsArray,
        expectedLearningHours: data.expectedLearningHours,
        difficultyRating: data.difficultyRating,
      };
      
      form.reset(formData);
      
      // Replace field arrays after form reset (use setTimeout to ensure form is reset first)
      setTimeout(() => {
        if (prerequisiteSkillsArray.length > 0) {
          replacePrerequisites(prerequisiteSkillsArray);
        }
        
        if (enablesSkillsArray.length > 0) {
          replaceEnables(enablesSkillsArray);
        }
      }, 100);
    },
    onError: () => {
      navigate('/admin/skills');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: SkillFormData) => {
      const payload: Partial<Skill> = {
        name: data.name,
        description: data.description,
        summary: data.summary,
        category: data.category,
        domain: data.domain,
        availableSkillLevels: data.availableSkillLevels,
        status: data.status,
        isActive: data.isActive,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        parentSkillId: (data.parentSkillId === '__none__' || !data.parentSkillId) ? undefined : data.parentSkillId,
        prerequisiteSkills: data.prerequisiteSkills?.filter(Boolean) || [],
        enablesSkills: data.enablesSkills?.filter(Boolean) || [],
        expectedLearningHours: data.expectedLearningHours,
        difficultyRating: data.difficultyRating,
      };
      return await skillApi.create(payload as Omit<Skill, 'id' | 'createdAt' | 'updatedAt'>);
    },
    entityName: 'skill',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/skills');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: SkillFormData) => {
      if (!id) throw new Error('Skill ID is required');
      const payload: Partial<Skill> = {
        name: data.name,
        description: data.description,
        summary: data.summary,
        category: data.category,
        domain: data.domain,
        availableSkillLevels: data.availableSkillLevels,
        status: data.status,
        isActive: data.isActive,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        parentSkillId: (data.parentSkillId === '__none__' || !data.parentSkillId) ? undefined : data.parentSkillId,
        prerequisiteSkills: data.prerequisiteSkills?.filter(Boolean) || [],
        enablesSkills: data.enablesSkills?.filter(Boolean) || [],
        expectedLearningHours: data.expectedLearningHours,
        difficultyRating: data.difficultyRating,
      };
      return await skillApi.update(id, payload);
    },
    entityName: 'skill',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/skills');
    },
  });

  const onSubmit = async (data: SkillFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingSkill;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading skill..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && skillError) {
    return <ErrorState error={skillError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/skills">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Skill' : 'Create Skill'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update skill information' : 'Create a new skill'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit as any)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the skill</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control as any}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter skill name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter skill description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter skill summary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control as any}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control as any}
                  name="domain"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Domain *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select domain" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DOMAINS.map((domain) => (
                            <SelectItem key={domain} value={domain}>
                              {domain.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control as any}
                name="availableSkillLevels"
                render={() => (
                  <FormItem>
                    <FormLabel>Available Skill Levels *</FormLabel>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {SKILL_LEVELS.map((level) => (
                        <FormField
                          key={level}
                          control={form.control as any}
                          name="availableSkillLevels"
                          render={({ field }) => {
                            return (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <input
                                    type="checkbox"
                                    checked={field.value?.includes(level)}
                                    onChange={(e) => {
                                      const current = field.value || [];
                                      if (e.target.checked) {
                                        field.onChange([...current, level]);
                                      } else {
                                        field.onChange(current.filter((v: string) => v !== level));
                                      }
                                    }}
                                    className="h-4 w-4"
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal">
                                  {level.replace(/_/g, ' ')}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tags separated by commas" {...field} />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control as any}
                name="parentSkillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parent Skill</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === '__none__' ? undefined : value)}
                      value={field.value || '__none__'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={isLoadingSkills ? "Loading skills..." : "Select parent skill (optional)"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {availableSkills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name} {skill.id ? `(${skill.id})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select a parent skill from the available skills list
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control as any}
                  name="expectedLearningHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expected Learning Hours</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={10000}
                          placeholder="Enter hours"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control as any}
                  name="difficultyRating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Rating (1-10)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          placeholder="Enter rating"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {isEdit && (
                <FormField
                  control={form.control as any}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="DRAFT">Draft</SelectItem>
                          <SelectItem value="ACTIVE">Active</SelectItem>
                          <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                          <SelectItem value="ARCHIVED">Archived</SelectItem>
                          <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control as any}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <FormDescription>Mark this skill as active</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Skill Relationships</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Prerequisite Skills</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendPrerequisite('')}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Prerequisite
                  </Button>
                </div>
                {prerequisiteFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2 mb-2">
                    <FormField
                      control={form.control as any}
                      name={`prerequisiteSkills.${index}` as any}
                      render={({ field: fieldProps }) => (
                        <FormItem className="flex-1">
                          <Select
                            onValueChange={fieldProps.onChange}
                            value={fieldProps.value || ''}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={isLoadingSkills ? "Loading..." : "Select prerequisite skill"} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableSkills.map((skill) => (
                                <SelectItem key={skill.id} value={skill.id || ''}>
                                  {skill.name} {skill.id ? `(${skill.id})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePrerequisite(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Enables Skills</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendEnables('')}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Enabled Skill
                  </Button>
                </div>
                {enablesFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2 mb-2">
                    <Input
                      placeholder="Enter skill ID"
                      {...form.register(`enablesSkills.${index}`)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEnables(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/skills">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default SkillFormPage;

